import { useEffect, memo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { assign, actions, send, createMachine } from 'xstate';
import { atomWithMachine } from 'jotai-xstate';
import { Provider, atom, useAtom, createStore } from 'jotai';
import Honeybadger from '@honeybadger-io/js';
import { LoaderBalloonScreen } from '@crazyegginc/hatch';
import { fromUnixTime, differenceInMilliseconds, subMilliseconds } from 'date-fns';

import { AuthErrorPage } from '/src/features/_global/pages/AuthErrorPage';
import { NoAccountsErrorPage } from '/src/features/_global/pages/NoAccountsErrorPage';
import {
  decodedToken,
  goToLogin,
  goToCurrentDashboard,
  semrushToCeToken,
  updateClockSkewFromApi,
} from '/src/utils/auth';
import { isBotUserAgent } from '/src/utils/honeybadger';
import { getShareTokenPayload, getShareCodeFromUrl, isTransportErrorLike, checkSharingSession } from '/src/utils';
import { AUTH_TOKEN_EXPIRATION_OFFSET_MS } from '/src/features/_global/constants';
import { registerTokenRefreshRequester, requestTokenRefresh } from '/src/services/token';
import {
  registerUserDataRefreshRequester,
  resolveUserDataRefresh,
  rejectUserDataRefresh,
} from '/src/services/user-data-refresh';
import {
  handleFetchedToken,
  handleAppAuthenticatedEntry,
  handleAccountSwitchSuccess,
  handleUnauthenticatedEntry,
  handleLoggedOutEntry,
  handleAccountErrorEntry,
  handleMachineErrorEntry,
  handleSharedResourcePresented,
  handleSharedResourceNotFound,
  redirectToSharedResource,
} from '/src/machines/auth-effects';
import { sharedResourceQuery } from '/src/features/_global/queries';
import { meQuery, sessionInfoQuery, capabilitiesQuery, subscriptionQuery } from '/src/features/_global/queries';
import { createTokenLifecycleMachine } from '/src/machines/token-lifecycle';
import { createStartupLifecycleMachine } from '/src/machines/startup-lifecycle';
import { newClient } from '/src/graphql/urql';
import { AppConfig } from '../AppConfig';

// states
const INITIALIZE_SHARING = 'INITIALIZE_SHARING';
const INITIALIZE_APP = 'INITIALIZE_APP';
const FETCH_TOKEN = 'FETCH_TOKEN';
const FETCH_SHARED_RESOURCE = 'FETCH_SHARED_RESOURCE';
const AUTHENTICATED = 'AUTHENTICATED';
const APP_AUTHENTICATED = 'APP_AUTHENTICATED';
const UNAUTHENTICATED = 'UNAUTHENTICATED';
const ACCOUNT_ERROR = 'ACCOUNT_ERROR';
export const FETCH_INITIAL_DATA = 'FETCH_INITIAL_DATA';
export const ERROR = 'ERROR';
export const PRESENT_SHARED_RESOURCE = 'PRESENT_SHARED_RESOURCE';
export const REDIRECT_TO_SHARED_RESOURCE = 'REDIRECT_TO_SHARED_RESOURCE';
export const SHARED_RESOURCE_NOT_FOUND = 'SHARED_RESOURCE_NOT_FOUND';
const HANDLE_SHARED_RESOURCE = 'HANDLE_SHARED_RESOURCE';
const LOGGED_OUT = 'LOGGED_OUT';

// internal actions
const INITIALIZED = 'INITIALIZED';
// const TOKEN = 'TOKEN';
const RECEIVED_SHARED_RESOURCE = 'RECEIVED_SHARED_RESOURCE';
// exposed actions
export const REFETCH_TOKEN = 'REFETCH_TOKEN';
export const REFETCH_SHARED_RESOURCE = 'REFETCH_SHARED_RESOURCE';
export const LOGOUT = 'LOGOUT';
export const SWITCH_ACCOUNT = 'SWITCH_ACCOUNT';
export const REFETCH_USER_DATA = 'REFETCH_USER_DATA';

const initialAuthAtom = atom(INITIALIZE_APP); // alternatively can be `INITIALIZE_SHARING`

const AUTH_ERROR_MESSAGES = ['Expired token', 'Invalid token', 'Malformed token', 'No user session'];
const SEMRUSH_TOKEN_REFRESH_LEEWAY_MS = 2 * 60 * 1000;
const STARTUP_FLOW_RETRY_COUNT = 1;

const reportedStartupErrors = new Set();

function tokenRefreshOffsetMs(isSemrushSession) {
  return isSemrushSession ? SEMRUSH_TOKEN_REFRESH_LEEWAY_MS : AUTH_TOKEN_EXPIRATION_OFFSET_MS;
}

function getErrorType(error) {
  return error?.graphQLErrors?.[0]?.originalError?.type || error?.graphQLErrors?.[0]?.extensions?.type;
}

function getErrorMessage(error) {
  return error?.graphQLErrors?.[0]?.originalError?.message || error?.message || '';
}

function isAuthError(error) {
  const message = getErrorMessage(error);
  const type = getErrorType(error);
  return AUTH_ERROR_MESSAGES.some((msg) => message.indexOf(msg) > -1) || type === 'authentication';
}

function getTokenLifecyclePayload(event) {
  return event?.data?.data ?? null;
}

function getStartupLifecyclePayload(event) {
  return event?.data?.data ?? null;
}

function getStartupVisibleAccounts(startupData, context) {
  const accounts = startupData?.me?.accounts ?? [];

  if (startupData?.sessionInfo?.isImpersonated || context?.isSemrush) {
    return accounts;
  }

  return accounts.filter((account) => context?.permittedAccounts?.includes(account.id));
}

async function fetchStartupData() {
  const client = newClient();
  const startupQueries = [
    { key: 'me', query: meQuery().meta.query },
    { key: 'sessionInfo', query: sessionInfoQuery().meta.query },
    { key: 'capabilities', query: capabilitiesQuery().meta.query },
    { key: 'subscription', query: subscriptionQuery().meta.query },
  ];

  const startupResults = await Promise.all(
    startupQueries.map(async ({ key, query }) => {
      const result = await client.query(query, {});
      return { key, result };
    }),
  );

  const startupData = {};
  const startupQueryErrors = {};

  startupResults.forEach(({ key, result }) => {
    if (result.error) {
      startupQueryErrors[key] = result.error;
      return;
    }

    startupData[key] = result.data?.[key] ?? null;
  });

  if (Object.keys(startupQueryErrors).length > 0) {
    reportStartupErrors(startupQueryErrors, startupQueries.length);
    const firstError = Object.values(startupQueryErrors)[0];
    const authError = Object.values(startupQueryErrors).find((error) => isAuthError(error));
    throw authError ?? firstError;
  }

  return startupData;
}

function reportStartupErrors(startupQueryErrors, totalStartupOperationsCount) {
  const failedStartupEntries = Object.entries(startupQueryErrors).filter(([, errorValue]) => Boolean(errorValue));
  const errors = failedStartupEntries.map(([, errorValue]) => errorValue);
  const failedOperations = failedStartupEntries.map(([operation]) => operation);
  const firstError = errors[0];
  const hasAuthError = errors.some(isAuthError);
  const isBotUser = isBotUserAgent();

  const message = getErrorMessage(firstError);
  const wholeStartupFailed = failedStartupEntries.length === totalStartupOperationsCount;
  const allTransportErrors = failedStartupEntries.every(([, errorValue]) => isTransportErrorLike(errorValue));
  const uniqueTransportMessages = new Set(
    failedStartupEntries.map(([, errorValue]) => getErrorMessage(errorValue)).filter(Boolean),
  );
  const allTransportErrorsShareMessage = allTransportErrors && uniqueTransportMessages.size === 1;
  const errorClassification = allTransportErrors ? 'transport' : 'application';

  if (message && !isBotUser && !hasAuthError && !['User not found'].some((msg) => message.indexOf(msg) > -1)) {
    const fingerprint = [
      'initial-data-fetch',
      errorClassification,
      ...failedOperations.sort((a, b) => a.localeCompare(b)),
    ].join('-');

    if (!reportedStartupErrors.has(fingerprint)) {
      reportedStartupErrors.add(fingerprint);
      Honeybadger.notify(`Initial data fetch ${errorClassification} error`, {
        fingerprint,
        context: {
          local_time: new Date().getTime(),
          message,
          pathname: window.location.pathname,
          online: navigator.onLine,
          gql_origin: window.CORE_API_URL ?? null,
          gql_url: window.CORE_API_URL ? `${window.CORE_API_URL}/gql` : null,
          failed_operations: failedOperations,
          wholeStartupFailed,
          allTransportErrors,
          allTransportErrorsShareMessage,
          startup_errors: Object.fromEntries(
            Object.entries(startupQueryErrors).map(([operation, operationError]) => [
              operation,
              serializeStartupError(operationError),
            ]),
          ),
        },
      });
    }
  } else if (!isBotUser && allTransportErrors) {
    Honeybadger.addBreadcrumb('Initial data fetch transport failure suppressed', {
      category: 'error',
      metadata: {
        pathname: window.location.pathname,
        online: navigator.onLine,
        failedOperations,
        message,
      },
    });
  }
}

function serializeStartupError(error) {
  if (!error) return null;

  return {
    name: error?.name,
    message: getErrorMessage(error),
    graphQLErrors: (error?.graphQLErrors ?? []).map((graphQLError) => ({
      message: graphQLError?.message,
      type: graphQLError?.originalError?.type || graphQLError?.extensions?.type,
      path: graphQLError?.path,
    })),
    networkError: error?.networkError
      ? {
          message: error.networkError.message,
          statusCode: error.networkError.statusCode,
        }
      : null,
  };
}

function tokenMsUntilExpiration(accessToken, eventData = {}) {
  if (!accessToken) return 0;

  const payload = decodedToken(accessToken);
  if (!payload?.exp) return 0;

  const localTime = new Date();

  if (eventData?.is_semrush) {
    const skewMs = (eventData?.clockSkewSeconds ?? 0) * 1000;
    const adjustedLocalTime = subMilliseconds(localTime, skewMs);
    return differenceInMilliseconds(fromUnixTime(payload.exp), adjustedLocalTime);
  }

  return payload.exp * 1000 - localTime.getTime();
}

async function invokeFetchAuthToken() {
  // if the user is a SEMRush user return early with the supplied token
  if (window.SM) {
    try {
      let rawToken = await window.SM.client('getAccessToken');
      let token = semrushToCeToken(rawToken);
      let clockSkewSeconds = 0;

      try {
        const skew = await updateClockSkewFromApi();
        clockSkewSeconds = Number.isFinite(skew) ? skew : 0;
      } catch (err) {
        Honeybadger.notify('Failed to get clock skew from API for SEMRush token', {
          context: {
            error: err.message,
          },
        });
      }

      const msUntilSemrushTokenExpiration = tokenMsUntilExpiration(token.access_token, {
        ...token,
        clockSkewSeconds,
      });

      if (msUntilSemrushTokenExpiration <= tokenRefreshOffsetMs(true)) {
        rawToken = await window.SM.client('getAccessToken');
        const refreshedToken = semrushToCeToken(rawToken);
        const refreshedTokenExpirationMs = tokenMsUntilExpiration(refreshedToken.access_token, {
          ...refreshedToken,
          clockSkewSeconds,
        });

        if (refreshedTokenExpirationMs > msUntilSemrushTokenExpiration) {
          token = refreshedToken;
        }
      }

      return { ...token, clockSkewSeconds };
    } catch (err) {
      const classification = isTransportErrorLike(err) ? 'transport' : 'application';
      Honeybadger.notify(`Semrush auth fetch ${classification} failed`, {
        fingerprint: `semrush-auth-fetch-${classification}`,
        context: {
          err: getErrorMessage(err),
          online: navigator.onLine,
          auth_base_url: AppConfig.authBaseURL(),
        },
      });
    }
  }

  try {
    const response = await fetch(`${AppConfig.authBaseURL()}/authenticate`, {
      method: 'GET',
      credentials: 'include',
      mode: 'cors',
      redirect: 'follow',
    });

    if (response.redirected) {
      return window.location.replace(response.url);
    }

    if (!response.ok) {
      let errorResponse = {};

      try {
        errorResponse = await response.json();
      } catch {
        errorResponse = {};
      }

      if (response.status === 401 || errorResponse?.error === 'unauthorized') {
        return { error: 'unauthorized' };
      }

      return Object.keys(errorResponse).length > 0 ? errorResponse : { error: 'failed_auth_fetch' };
    }

    const token = await response.json();

    return token;
  } catch (error) {
    const classification = isTransportErrorLike(error) ? 'transport' : 'application';
    if (classification !== 'transport') {
      Honeybadger.notify(`User auth fetch ${classification} failed`, {
        fingerprint: `user-auth-fetch-${classification}`,
        context: {
          error: error.message,
          stack: error.stack,
          online: navigator.onLine,
          auth_base_url: AppConfig.authBaseURL(),
        },
      });
    }
    throw new Error('User auth fetch failed');
  }
}

async function performAccountSwitch(context, event) {
  const rawNewToken = await fetch(`${AppConfig.authBaseURL()}/switch?account_holder_id=${event.accountId}`, {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
  });
  return await rawNewToken.json();
}

export async function signOut() {
  return window.location.replace(`${AppConfig.authBaseURL()}/logout`);
}

function handleAccountMessage({ message }) {
  if (message === 'logout') {
    goToLogin();
  } else if (message === 'accountSwitch') {
    goToCurrentDashboard();
  } else if (message === 'unauthenticated') {
    goToLogin(true);
  }
}

const initialAuthContext = {
  token: null,
  connected: false,
  isSharing: false,
  sessionInfo: null,
  currentUser: null,
  currentAccount: null,
  sharedResource: null,
  refetchAttempts: 0,
  sharingCode: null,
  msUntilTokenExpiration: null,
  permittedAccounts: [],
  isSemrush: false,
};

export const createAuthMachine = (initial) => {
  return createMachine(
    {
      id: 'auth',
      // https://xstate.js.org/docs/guides/actions.html
      predictableActionArguments: true,
      preserveActionOrder: true,
      initial,
      context: initialAuthContext,
      on: {
        [REFETCH_TOKEN]: {
          target: FETCH_TOKEN,
        },
        [REFETCH_SHARED_RESOURCE]: {
          target: FETCH_SHARED_RESOURCE,
        },
      },
      states: {
        // initial states
        [INITIALIZE_APP]: {
          on: {
            [INITIALIZED]: {
              actions: [
                assign({
                  isSharing: false,
                }),
              ],
              target: FETCH_TOKEN,
            },
          },
        },
        [INITIALIZE_SHARING]: {
          on: {
            [INITIALIZED]: {
              actions: [
                assign({
                  isSharing: true,
                }),
              ],
              target: FETCH_SHARED_RESOURCE,
            },
          },
        },
        // loading states
        [FETCH_TOKEN]: {
          on: {
            [REFETCH_TOKEN]: {
              actions: [],
            },
          },
          invoke: {
            id: 'token-lifecycle',
            src: () =>
              createTokenLifecycleMachine({
                fetchAuthToken: invokeFetchAuthToken,
                tokenMsUntilExpiration,
                maxRetries: 3,
              }),
            onDone: [
              {
                target: AUTHENTICATED,
                cond: 'isValidTokenLifecycleJwt',
                actions: [
                  assign({
                    refetchAttempts: 0,
                    token: (_context, event) => getTokenLifecyclePayload(event)?.access_token,
                    tokenPayload: (context, event) =>
                      getTokenLifecyclePayload(event)?.access_token
                        ? decodedToken(getTokenLifecyclePayload(event).access_token)
                        : null,
                    permittedAccounts: (_context, event) => getTokenLifecyclePayload(event)?.permitted_accounts ?? [],
                    isSemrush: (_context, event) => getTokenLifecyclePayload(event)?.is_semrush ?? false,
                  }),
                  assign({
                    msUntilTokenExpiration: (_context, event) => {
                      const tokenData = getTokenLifecyclePayload(event);
                      return tokenMsUntilExpiration(tokenData?.access_token, tokenData);
                    },
                  }),
                  (_context, event) => {
                    handleFetchedToken(getTokenLifecyclePayload(event));
                  },
                ],
              },
              {
                target: UNAUTHENTICATED,
                cond: 'isUnauthorizedTokenLifecycleResult',
              },
              {
                target: UNAUTHENTICATED,
                cond: 'isNoAccessTokenLifecycleResult',
              },
              {
                target: ACCOUNT_ERROR,
                cond: (_ctx, event) => {
                  const tokenData = getTokenLifecyclePayload(event);
                  if (
                    tokenData?.access_token &&
                    tokenData?.permitted_accounts &&
                    tokenData.permitted_accounts.length === 0
                  ) {
                    return true;
                  }
                  return false;
                },
              },
              { target: UNAUTHENTICATED },
            ],
          },
        },
        RETRY_FETCH_INITIAL_DATA: {
          after: {
            REFETCH_DELAY: {
              actions: [
                assign({
                  refetchAttempts: (context) => context.refetchAttempts + 1,
                }),
              ],
              target: FETCH_INITIAL_DATA,
            },
          },
        },
        [FETCH_SHARED_RESOURCE]: {
          entry: [
            assign({
              sharingCode: getShareCodeFromUrl() || getShareTokenPayload()?.shared_item?.code,
            }),
            actions.pure((context) => {
              if (!context.sharingCode || context.sharingCode === '') {
                return send('SHARED_RESOURCE_NOT_FOUND');
              }
            }),
          ],
          on: {
            [RECEIVED_SHARED_RESOURCE]: {
              target: HANDLE_SHARED_RESOURCE,
              actions: [
                assign({
                  sharedResource: (context, event) => event.data?.sharedResource,
                }),
              ],
            },
            [SHARED_RESOURCE_NOT_FOUND]: {
              target: SHARED_RESOURCE_NOT_FOUND,
            },
          },
        },
        [HANDLE_SHARED_RESOURCE]: {
          always: [
            {
              target: PRESENT_SHARED_RESOURCE,
              cond: 'hasPresentableSharedResource',
            },
            {
              target: REDIRECT_TO_SHARED_RESOURCE,
              cond: 'hasRedirectableSharedResource',
            },
            { target: SHARED_RESOURCE_NOT_FOUND },
          ],
        },
        [PRESENT_SHARED_RESOURCE]: {
          on: {},
          entry: [
            assign({
              token: (ctx) => ctx.sharedResource.resource.authToken,
            }),
            (ctx) => {
              handleSharedResourcePresented(ctx.sharedResource.resource.authToken);
            },
          ],
        },
        [REDIRECT_TO_SHARED_RESOURCE]: {
          type: 'final',
          entry: [
            (context) => {
              redirectToSharedResource(context.sharedResource);
            },
          ],
        },
        [SHARED_RESOURCE_NOT_FOUND]: {
          type: 'final',
          entry: [
            () => {
              handleSharedResourceNotFound();
            },
          ],
        },
        [FETCH_INITIAL_DATA]: {
          invoke: {
            id: 'startup-lifecycle',
            src: (context) =>
              createStartupLifecycleMachine({
                fetchInitialData: () => fetchStartupData(),
                hasValidInitialData: (data) => {
                  const visibleAccounts = getStartupVisibleAccounts(data, context);

                  return Boolean(
                    data?.sessionInfo &&
                      data?.me &&
                      data?.capabilities &&
                      data?.subscription &&
                      visibleAccounts.length > 0 &&
                      visibleAccounts.some((account) => account.isCurrent),
                  );
                },
                isAuthError,
              }),
            onDone: [
              {
                actions: [
                  assign({
                    refetchAttempts: 0,
                    currentUser: (context, event) => {
                      const startupData = getStartupLifecyclePayload(event);
                      const visibleAccounts = getStartupVisibleAccounts(startupData, context);

                      return {
                        ...startupData.me,
                        accounts: visibleAccounts,
                      };
                    },
                    sessionInfo: (_context, event) => getStartupLifecyclePayload(event).sessionInfo,
                    currentAccount: (context, event) => {
                      const startupData = getStartupLifecyclePayload(event);
                      const visibleAccounts = getStartupVisibleAccounts(startupData, context);
                      return visibleAccounts.find((account) => account.isCurrent);
                    },
                    capabilities: (_context, event) => getStartupLifecyclePayload(event).capabilities,
                    subscription: (_context, event) => getStartupLifecyclePayload(event).subscription,
                  }),
                ],
                target: AUTHENTICATED,
                cond: 'isStartupLifecycleSuccess',
              },
              {
                target: ACCOUNT_ERROR,
                cond: 'isStartupLifecycleNoAccounts',
              },
              {
                target: FETCH_TOKEN,
                cond: 'isStartupLifecycleAuthError',
              },
              {
                target: 'RETRY_FETCH_INITIAL_DATA',
                cond: 'canRetryInitialDataFetch',
              },
              {
                target: 'ERROR',
              },
            ],
          },
        },
        // final states
        [AUTHENTICATED]: {
          always: [
            {
              target: FETCH_INITIAL_DATA,
              cond: 'sessionNotLoaded',
            },
            {
              target: APP_AUTHENTICATED,
            },
          ],
        },
        [APP_AUTHENTICATED]: {
          entry: [
            (context) => {
              handleAppAuthenticatedEntry(context);
            },
          ],
          on: {
            [REFETCH_TOKEN]: {
              target: FETCH_TOKEN,
            },
            [REFETCH_USER_DATA]: {
              target: FETCH_INITIAL_DATA,
            },
            [LOGOUT]: {
              target: LOGGED_OUT,
            },
            [SWITCH_ACCOUNT]: {
              target: SWITCH_ACCOUNT,
            },
          },
          after: {
            TOKEN_EXPIRATION: {
              target: FETCH_TOKEN,
            },
          },
        },
        [SWITCH_ACCOUNT]: {
          invoke: {
            id: 'account-switch',
            src: performAccountSwitch,
            onDone: {
              // target: FETCH_INITIAL_DATA,
              actions: [
                assign({
                  token: (context, event) => event?.data?.access_token || event?.data?.token,
                  permittedAccounts: (context, event) => event?.data?.permitted_accounts ?? [],
                }),
                (ctx) => {
                  handleAccountSwitchSuccess(ctx.token);
                },
              ],
            },
          },
        },
        [UNAUTHENTICATED]: {
          type: 'final',
          entry: [
            assign(initialAuthContext),
            () => {
              handleUnauthenticatedEntry();
            },
          ],
        },
        [LOGGED_OUT]: {
          type: 'final',
          entry: [
            assign(initialAuthContext),
            () => {
              handleLoggedOutEntry(signOut);
            },
          ],
        },
        [ACCOUNT_ERROR]: {
          type: 'final',
          entry: [
            () => {
              handleAccountErrorEntry();
            },
          ],
        },
        [ERROR]: {
          type: 'final',
          entry: [
            assign({
              refetchAttempts: 0,
            }),
            () => {
              handleMachineErrorEntry();
            },
            // we could do some Honeybadger reporting here
          ],
        },
      },
    },
    {
      delays: {
        TOKEN_EXPIRATION: (context) => {
          const refreshOffsetMs = tokenRefreshOffsetMs(context.isSemrush);

          // fetch before token expiration using the shared auth offset
          if (context.msUntilTokenExpiration <= refreshOffsetMs) {
            // immediately trigger expiration
            return 0;
          }
          return context.msUntilTokenExpiration - refreshOffsetMs;
        },
        REFETCH_DELAY: (ctx) => {
          return ctx.refetchAttempts * 1000;
        },
      },
      guards: {
        hasPermittedAccounts: (context, event) => {
          if (event?.data?.permitted_accounts && event.data.permitted_accounts.length > 0) {
            return true;
          }
          return false;
        },
        isValidTokenLifecycleJwt: (context, event) => {
          try {
            const tokenData = getTokenLifecyclePayload(event);

            if (event?.data?.status === 'success' && tokenData?.access_token) {
              const tokenPayload = decodedToken(tokenData.access_token);
              if (!tokenPayload) {
                return false;
              }

              if (tokenMsUntilExpiration(tokenData.access_token, tokenData) <= 0) {
                return false;
              }

              if (tokenData.is_semrush) {
                // skip permitted accounts checks for SEMRush users
                return true;
              }

              if (tokenData.permitted_accounts.length === 0) {
                return false;
              }

              if (tokenData.permitted_accounts.includes(tokenPayload.sub)) {
                return true;
              }

              Honeybadger.notify('User attempted to use invalid JWT token', {
                context: {
                  token: tokenData.access_token,
                  permitted_accounts: tokenData.permitted_accounts,
                },
              });
            }
            return false;
          } catch (error) {
            Honeybadger.notify('Failed to check JWT token', {
              context: {
                payload: {
                  ...context,
                },
                event: {
                  ...event,
                },
                error: error.message,
                stack: error.stack,
              },
            });
            return false;
          }
        },
        isUnauthorizedTokenLifecycleResult: (_context, event) => {
          return event?.data?.status === 'unauthorized';
        },
        isNoAccessTokenLifecycleResult: (_context, event) => {
          return event?.data?.status === 'no_access_token';
        },
        canRetryInitialDataFetch: (context) => context.refetchAttempts < STARTUP_FLOW_RETRY_COUNT,
        isStartupLifecycleSuccess: (_context, event) => event?.data?.status === 'success',
        isStartupLifecycleNoAccounts: (_context, event) => event?.data?.status === 'no_accounts',
        isStartupLifecycleAuthError: (_context, event) => event?.data?.status === 'auth_error',
        sessionNotLoaded: (context) => !context.currentUser,
        hasPresentableSharedResource: (context) => {
          return context.sharedResource.type === 'RESOURCE';
        },
        hasRedirectableSharedResource: (context) => {
          return context.sharedResource.type === 'REDIRECT';
        },
      },
    },
  );
};

// this is the only way you should interact with the authMachine in any way from
// anywhere in the app.
export const authAtom = atomWithMachine((get) => createAuthMachine(get(initialAuthAtom)), {
  devTools: import.meta.env.DEV,
});

// The Jotai provider in this component is an isolated tree,
// it also provides the initial values to the contained machine.
// It is wrapped in a Suspense component because there are
// async behaviours contained inside.
// NOTE: please be sure that there is only ever one AuthProvider in the tree at any given moment.
export const AuthProvider = memo(({ children }) => {
  const isSharedSession = checkSharingSession();

  if (!isSharedSession) {
    window.ce_sharingSession = false;
  }

  const store = createStore();
  store.set(initialAuthAtom, isSharedSession ? INITIALIZE_SHARING : INITIALIZE_APP);

  return (
    <Provider store={store}>
      <AuthFlow>{children}</AuthFlow>
    </Provider>
  );
});

function AdminAuthFlow() {
  useEffect(() => {
    // remove anything that might hinder impesonation sessions
    window.sessionStorage.clear();
    window.localStorage.clear();

    // get redirectTo from query params and include any other params in the destinatin url
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get('redirectTo');
    const destination = new URL(redirectTo || '/', window.location.origin);
    params.forEach((value, key) => {
      if (key === 'redirectTo') return;
      destination.searchParams.set(key, value);
    });
    window.location.replace(destination.toString());
  }, []);

  return <LoaderBalloonScreen text={`Preparing impersonation...`} />;
}

function UserAuthFlow({ children }) {
  // ensure the machine starts up
  const [state, send] = useAtom(authAtom);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (window.location.search.indexOf('debug=auth') >= 0) {
      Honeybadger.notify('Auth debug', {
        context: {
          value: state.value,
          state: state.context,
        },
      });
    }
  }, [state]);

  const sharedResourceResult = useQuery({
    ...sharedResourceQuery({ sharingCode: state.context.sharingCode }),
    enabled: Boolean(state.value === FETCH_SHARED_RESOURCE && state.context.sharingCode),
  });

  useEffect(() => {
    if (state.value === INITIALIZE_APP || state.value === INITIALIZE_SHARING) {
      send({
        type: INITIALIZED,
      });
    }
  }, [send, state.value]);

  useEffect(() => {
    if (state.value === FETCH_SHARED_RESOURCE && sharedResourceResult?.data) {
      send({
        type: RECEIVED_SHARED_RESOURCE,
        data: sharedResourceResult.data,
      });
    } else if (state.value === FETCH_SHARED_RESOURCE && sharedResourceResult?.error) {
      send({
        type: SHARED_RESOURCE_NOT_FOUND,
      });
    }
  }, [send, sharedResourceResult?.data, sharedResourceResult?.error, state.value]);

  useEffect(() => {
    window.accountChannel?.addEventListener?.('message', handleAccountMessage);

    return () => window.accountChannel?.removeEventListener?.('message', handleAccountMessage);
  }, []);

  useEffect(() => {
    const unregisterRefreshRequester = registerTokenRefreshRequester(() => {
      send({ type: state.context.isSharing ? REFETCH_SHARED_RESOURCE : REFETCH_TOKEN });
    });

    return unregisterRefreshRequester;
  }, [send, state.context.isSharing]);

  useEffect(() => {
    const unregisterRefreshRequester = registerUserDataRefreshRequester(() => {
      send({ type: REFETCH_USER_DATA });
    });

    return unregisterRefreshRequester;
  }, [send]);

  useEffect(() => {
    if (state.value === APP_AUTHENTICATED && state.context.currentUser && state.context.currentAccount) {
      resolveUserDataRefresh({
        currentUser: state.context.currentUser,
        currentAccount: state.context.currentAccount,
      });
    }

    if ([UNAUTHENTICATED, LOGGED_OUT, ACCOUNT_ERROR, ERROR].includes(state.value)) {
      rejectUserDataRefresh(new Error('User data refresh failed'));
    }
  }, [state.value, state.context.currentUser, state.context.currentAccount]);

  useEffect(() => {
    if (
      !state.context.currentUser ||
      !state.context.sessionInfo ||
      !state.context.capabilities ||
      !state.context.subscription
    ) {
      return;
    }

    queryClient.setQueryData(meQuery().queryKey, { me: state.context.currentUser });
    queryClient.setQueryData(sessionInfoQuery().queryKey, { sessionInfo: state.context.sessionInfo });
    queryClient.setQueryData(capabilitiesQuery().queryKey, { capabilities: state.context.capabilities });
    queryClient.setQueryData(subscriptionQuery().queryKey, { subscription: state.context.subscription });
  }, [
    queryClient,
    state.context.currentUser,
    state.context.sessionInfo,
    state.context.capabilities,
    state.context.subscription,
  ]);

  // we can declare global type of listeners here
  // but please keep them to a minimum 😅

  if ([SWITCH_ACCOUNT, FETCH_INITIAL_DATA, FETCH_SHARED_RESOURCE, 'RETRY_FETCH_INITIAL_DATA'].includes(state.value)) {
    return (
      <>
        {state.context.refetchAttempts > 0 && state.context.refetchAttempts <= 3 ? (
          <LoaderBalloonScreen text={`Retry ${state.context.refetchAttempts} of 3...`} />
        ) : state.context.refetchAttempts > 0 ? (
          <LoaderBalloonScreen text={false} />
        ) : (
          <LoaderBalloonScreen text={true} />
        )}
      </>
    );
  }

  if (state.value === ACCOUNT_ERROR) {
    return <NoAccountsErrorPage />;
  }

  if (state.value === ERROR) {
    return <AuthErrorPage />;
  }

  if (!state.context?.currentUser && !state.context.isSharing) {
    return <LoaderBalloonScreen text={true} />;
  }

  return children;
}

function AuthFlow({ children }) {
  const { pathname } = window.location;

  if (pathname === '/admin/impersonate') {
    return <AdminAuthFlow />;
  }

  return <UserAuthFlow>{children}</UserAuthFlow>;
}

export function refetchAuthToken() {
  return requestTokenRefresh();
}
